import { useState } from "react";
import { isEmpty } from "lodash";
import { t } from "@/i18n-js/instance";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useConfirmMigrationModal } from "../ConfirmMigrationModal";
import { MIGRATION_FLOW_OPTIONS } from "../constants";
import { useSanitizeFormPayload } from "./useSanitizeFormPayload";

const i18nBase = "settings.access_groups.paywall_migration.modal.form";

interface UseMigrationFormProps {
  closeModals: () => void;
  shouldShowPaywallGroupSelector: boolean;
}

// Helper validation functions to reduce cognitive complexity
const validatePaywallGroup = (
  formValues: ReturnType<typeof useSanitizeFormPayload>["formValues"],
  toastr: ReturnType<typeof useToast>,
) => {
  const paywall_group_name = formValues.paywall_group_name || "";
  const paywall_group_id = formValues.paywall_group_id;

  const hasValidPaywallGroup =
    !isEmpty(paywall_group_id) ||
    (!isEmpty(paywall_group_name) && paywall_group_name.trim() !== "");

  if (!hasValidPaywallGroup) {
    toastr.error(t([i18nBase, "validations.subscription_group_required"]));
    return false;
  }

  // Check the max length only if using custom name (not ID)
  if (
    isEmpty(paywall_group_id) &&
    !isEmpty(paywall_group_name) &&
    paywall_group_name.length > 65
  ) {
    toastr.error(
      t([i18nBase, "validations.subscription_group_name_max_length"]),
    );
    return false;
  }

  return true;
};

const validateAccessGroup = (
  formValues: ReturnType<typeof useSanitizeFormPayload>["formValues"],
  isCreatingAccessGroup: boolean,
  toastr: ReturnType<typeof useToast>,
) => {
  const name = formValues.name || "";
  const isEmptyName = isEmpty(name) || name.trim() === "";
  const isNameTooLong = !isEmpty(name) && name.length > 65;

  if (isCreatingAccessGroup && isEmptyName) {
    toastr.error(t([i18nBase, "validations.name_required"]));
    return false;
  }

  if (isCreatingAccessGroup && isNameTooLong) {
    toastr.error(t([i18nBase, "validations.name_max_length"]));
    return false;
  }

  if (!isCreatingAccessGroup && isEmpty(formValues.access_group_ids)) {
    toastr.error(t([i18nBase, "validations.access_group_required"]));
    return false;
  }

  return true;
};

const validateForm = (
  formValues: ReturnType<typeof useSanitizeFormPayload>["formValues"],
  isCreatingAccessGroup: boolean,
  toastr: ReturnType<typeof useToast>,
  isPaywallGroupStep: boolean,
  isAccessGroupConfigurationStep: boolean,
) => {
  if (isPaywallGroupStep) {
    return validatePaywallGroup(formValues, toastr);
  }

  if (isAccessGroupConfigurationStep) {
    return validateAccessGroup(formValues, isCreatingAccessGroup, toastr);
  }

  return true;
};

const getStepConfig = (shouldShowPaywallGroupSelector: boolean) => ({
  subscription: 0,
  select: shouldShowPaywallGroupSelector ? 1 : 0,
  review: shouldShowPaywallGroupSelector ? 2 : 1,
  totalSteps: shouldShowPaywallGroupSelector ? 3 : 2,
});

export const useMigrationForm = ({
  closeModals,
  shouldShowPaywallGroupSelector,
}: UseMigrationFormProps) => {
  const confirmMigrationModal = useConfirmMigrationModal();
  const { formValues } = useSanitizeFormPayload();
  const toastr = useToast();
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  const isCreatingAccessGroup =
    formValues.migration_flow === MIGRATION_FLOW_OPTIONS.new_access_group;

  const steps = getStepConfig(shouldShowPaywallGroupSelector);

  const isPaywallGroupSelectionStep =
    selectedStepIndex === steps.subscription && shouldShowPaywallGroupSelector;
  const isAccessGroupConfigurationStep = selectedStepIndex === steps.select;
  const isReviewChangesStep = selectedStepIndex === steps.review;

  const isFormValid = () =>
    validateForm(
      formValues,
      isCreatingAccessGroup,
      toastr,
      isPaywallGroupSelectionStep,
      isAccessGroupConfigurationStep,
    );

  const handleStepChange = (index: number) => {
    if (isFormValid()) {
      setSelectedStepIndex(index);
    }
  };

  const handleReviewStep = () => {
    void confirmMigrationModal.show({
      formValues,
      closeModals,
    });
  };

  const getNextStep = () => {
    if (isPaywallGroupSelectionStep) return steps.select;
    if (isAccessGroupConfigurationStep) return steps.review;
    return null;
  };

  const handleFormNavigation = () => {
    if (!isReviewChangesStep) {
      const nextStep = getNextStep();
      if (nextStep !== null && isFormValid()) {
        setSelectedStepIndex(nextStep);
      }
      return;
    }

    if (isFormValid()) {
      handleReviewStep();
    }
  };

  const shouldShowBackButton =
    isReviewChangesStep ||
    (!isPaywallGroupSelectionStep && shouldShowPaywallGroupSelector);

  return {
    isCreatingAccessGroup,
    handleFormNavigation,
    isFormValid,
    handleStepChange,
    selectedStepIndex,
    isReviewChangesStep,
    isPaywallGroupSelectionStep,
    isAccessGroupConfigurationStep,
    totalSteps: steps.totalSteps,
    shouldShowBackButton,
  };
};
